import { ProposalImages } from "@/types/proposal";
import foundryLogo from "@/assets/foundry-logo.png";
import { useState, useCallback } from "react";

interface ProposalHeaderProps {
  images: ProposalImages;
  projectNumber: string;
  date: string;
  onLogoDrop?: (dataUrl: string) => void;
}

export const ProposalHeader = ({ images, projectNumber, date, onLogoDrop }: ProposalHeaderProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  
  // Use uploaded logo if available, otherwise use default
  const logoSrc = images.logo || foundryLogo;

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!onLogoDrop) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, [onLogoDrop]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!onLogoDrop) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, [onLogoDrop]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    if (!onLogoDrop) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    // Check if dropping from gallery (custom data)
    const galleryImageData = e.dataTransfer.getData('image/dataurl');
    if (galleryImageData) {
      onLogoDrop(galleryImageData);
      return;
    }

    // Check if dropping files from computer
    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(f => f.type.startsWith('image/'));
    
    if (imageFile) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        onLogoDrop(dataUrl);
      };
      reader.readAsDataURL(imageFile);
    }
  }, [onLogoDrop]);
  
  return (
    <div className="gradient-slate p-8 relative">
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent"></div>
      <div className="flex justify-between items-start">
        <div className="flex items-center space-x-6">
          <div 
            className={`bg-white p-3 shadow-xl border-2 transition-all ${
              isDragOver ? 'border-amber-500 ring-4 ring-amber-400 scale-105' : 'border-amber-400'
            } ${onLogoDrop ? 'cursor-pointer' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <img 
              src={logoSrc} 
              alt="Foundry Cabinet Co. Logo" 
              className="w-40 h-16 object-contain" 
            />
          </div>
          <div className="h-12 w-px bg-gradient-to-b from-transparent via-white to-transparent opacity-30"></div>
          <div className="text-white">
            <div className="text-xl font-bold tracking-wide drop-shadow-lg">FOUNDRY CABINET CO.</div>
            <div className="text-xs font-light tracking-wider text-amber-400">BY DISTRICT DESIGN BUILD, LLC</div>
          </div>
        </div>
        <div className="text-right text-white text-sm">
          <div className="font-bold tracking-wider">PROJECT {projectNumber}</div>
          <div className="text-slate-300 font-light">{date}</div>
        </div>
      </div>
    </div>
  );
};
